// import EditLicenseDialogSmall from "./EditLicenseDialogSmall";
// import EditLicenseDialogBig from "./EditLicenseDialogBig";
import { useCallback, useEffect, useState } from "react";
import { useApi } from "../../utils/Api";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Translate from "../../utils/Translate";
import ActorAvatar from "../ActorAvatar";
import Preloader from "../Preloader";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { useSnackbar } from "notistack";

const _minItemsPerColumn = 8;
const _maxColumns = 3;

export default function EditDigitalLicenseDialogBase({ digitalLicenseId, onClose }) {
  const [actorSkillsBySkillId, setActorSkillsBySkillId] = useState(null);
  const [availableSkills, setAvailableSkills] = useState(null);
  const [maxColumns, setMaxColumns] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const api = useApi();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(`(max-width:800px)`);
  const isMediumScreen = useMediaQuery(theme.breakpoints.up("sm") && theme.breakpoints.down("lg"));
  const dialogMaxWidth = isSmallScreen || maxColumns == 1 ? "xs" : isMediumScreen || maxColumns == 2 ? "md" : "lg";
  const skillSize = isSmallScreen || maxColumns == 1 ? 12 : isMediumScreen || maxColumns == 2 ? 6 : 4;

  useEffect(() => {
    async function init() {
      setIsLoading(true);
      const url = `${import.meta.env.VITE_MAIN_URL}digital/licenses/${digitalLicenseId}/for-edit`;
      const response = await api.fetch(url, false, "GET");
      if (response.isSuccessful) {
        setActorSkillsBySkillId(Object.fromEntries(response.actorSkills.map((a) => [a.skillId, a])));
        setAvailableSkills(response.availableSkills);
        setMaxColumns(Math.min(Math.ceil(response.availableSkills.length / _minItemsPerColumn), _maxColumns));
      }
      setIsLoading(false);
    }
    init();
  }, [api, digitalLicenseId]);

  const handleSave = useCallback(
    async (digitalLicenseId, actorSkillsBySkillId) => {
      const skillIds = Object.values(actorSkillsBySkillId)
        .filter((actorSkill) => actorSkill.hasPassed)
        .map((actorSkill) => actorSkill.skillId);
      const request = { digitalLicenseId, skillIds };
      const url = `${import.meta.env.VITE_MAIN_URL}digital/licenses/update`;
      const response = await api.fetch(url, request, "POST");
      if (response.isSuccessful) {
        const message = enqueueSnackbar(Translate.get("Ready"), {
          variant: "success",
          autoHideDuration: 6000,
          onClick: () => closeSnackbar(message),
        });
      }
    },
    [api, enqueueSnackbar]
  );

  const handleCheckSkillChange = useCallback((hasPassed, skillId) => {
    setActorSkillsBySkillId((prev) => ({ ...prev, [skillId]: { ...prev[skillId], hasPassed, skillId } }));
  }, []);

  if (isLoading) {
    return <Preloader />;
  }

  return (
    <>
      <Dialog open onClose={onClose} maxWidth={dialogMaxWidth}>
        <DialogTitle>{Translate.get("EditDigitalLicense")}</DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              marginTop: "10px",
              textTransform: "uppercase",
            }}
          >
            {Translate.get("Competences")}
          </Typography>

          <Grid container>
            {availableSkills.map((skill) => (
              <Grid item size={skillSize}>
                <FormControl>
                  <FormGroup>
                    <FormControlLabel
                      className="editDigitalLicenseSkillCheckbox"
                      control={
                        <Checkbox
                          checked={
                            !!actorSkillsBySkillId[skill.skillId] && actorSkillsBySkillId[skill.skillId].hasPassed
                          }
                          onChange={(event) => handleCheckSkillChange(event.target.checked, skill.skillId)}
                        />
                      }
                      label={
                        <Typography sx={{ marginLeft: "14px" }} noWrap>
                          {`${skill.shortCode} ${skill.description}`}
                        </Typography>
                      }
                      labelPlacement="end"
                      key={skill.skillId}
                    />
                  </FormGroup>
                </FormControl>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{Translate.get("Cancel")}</Button>
          <ButtonWithSpinner
            onClick={async () => await handleSave(digitalLicenseId, actorSkillsBySkillId)}
            variant="contained"
          >
            {Translate.get("Save")}
          </ButtonWithSpinner>
        </DialogActions>
      </Dialog>
    </>
  );
}
